import { MODULE_BRICK_BALANCE, MYSTERY_BRICK, REBIRTH_UPGRADE_DEFS } from "../data/balance.js";
import { expandedModulePool } from "../data/moduleDefinitions.js";
import { ChapterController } from "./ChapterController.js";
import { EconomyController } from "./EconomyController.js";
import { ModuleInventory } from "./ModuleInventory.js";
import { PlayerModuleLevelController } from "./PlayerModuleLevelController.js";
import { ModuleMasteryController } from "./ModuleMasteryController.js";
import { SynergyLevelController } from "./SynergyLevelController.js";
import { SpaceshipController } from "./SpaceshipController.js";
import { UpgradeInventory } from "../models/UpgradeInventory.js";
import { ModuleGridController } from "./ModuleGridController.js";
import { GoldModuleBox } from "./GoldModuleBox.js";
import { DiamondUpgradeBox } from "./DiamondUpgradeBox.js";
import { RebirthController } from "./RebirthController.js";
import { StatUpgradeController } from "./StatUpgradeController.js";
import { RelicController } from "./RelicController.js";
import { DungeonController } from "./DungeonController.js";
import { DailyShopController } from "./DailyShopController.js";
import { ModuleFusionController } from "./ModuleFusionController.js";
import { MysteryBlockController } from "./MysteryBlockController.js";
import { AnalyticsMilestoneController } from "./AnalyticsMilestoneController.js";
import { ModuleSynergyController } from "./ModuleSynergyController.js";
import { MODULE_BRICK_SAVE_VERSION, type ModuleBrickSaveData } from "../models/ModuleBrickSaveData.js";
import type { CooldownEntry } from "@yjsgames/cooldown";
import { GuideQuestController } from "@yjsgames/guide-quest";
import { GUIDE_QUEST_DEFINITIONS } from "../data/guideQuestData.js";

/**
 * 저장 직렬화/역직렬화 — GameScene에서 추출(god-object 분해). SaveData ↔ 게임 상태 묶음 매핑을
 * 한 곳에 모아 순수 함수로 만든다(Phaser 비의존, round-trip 단위테스트 가능). GameScene은 결과를
 * 필드에 분배(decode)하거나 묶음을 넘기기(encode)만 하고, side effect(setLanguage 등)는 GameScene이 담당.
 *
 * ⚠ 기기별 설정(soundVolumes/language/numberFormat)은 진행 데이터에서 분리됨 → BrickSettings(로컬 전용 BrickSettingsStore).
 * 본 codec은 진행 데이터(골드/챕터/모듈/던전 등)만 다룬다.
 */

/**
 * 골드 상자 생성 — 카탈로그 (id, rarity)를 등급 추첨 풀로. 새 게임(GameScene.create) + 저장 복원(decode) 공용.
 * 등급 천장(1개·10개 공용)도 여기서 주입 — pityInterval회마다 pityGuaranteeRarity 이상 보장, 카운터는
 * SaveData rarityPityCounter로 복원(로컬/firestore 공용 normalize 경로 → field-add 0 백필).
 */
export function createGoldBox(initial?: {
  boxOpenCount: number;
  pityCounter: number;
  rarityPityCounter: number;
}): GoldModuleBox {
  // 등급 추첨 풀 — 모듈이 effect 칸이 채워진 모든 등급으로 펼쳐짐(같은 모듈 여러 등급 등장 + 최소 등급).
  const defs = expandedModulePool();
  return new GoldModuleBox(
    defs,
    Math.random,
    undefined,
    MODULE_BRICK_BALANCE.guaranteedBoxOpenCount,
    initial,
    {
      interval: MODULE_BRICK_BALANCE.pityInterval,
      floorRarity: MODULE_BRICK_BALANCE.pityGuaranteeRarity,
    },
  );
}

/** 저장 데이터에서 복원된 게임 상태 묶음 (GameScene이 필드에 분배). */
export interface GameSaveState {
  readonly chapter: ChapterController;
  readonly economy: EconomyController;
  readonly inventory: ModuleInventory;
  /** 모듈 레벨 — moduleId별 플레이어 영구 자산(환생 유지). 인스턴스 등급과 직교(2축). */
  readonly playerModuleLevels: PlayerModuleLevelController;
  /** 모듈 숙련도 — moduleId별 누적 XP 기반 노드 해금(레벨·등급과 직교한 3번째 성장축). */
  readonly moduleMastery: ModuleMasteryController;
  /** 시너지 도감 발견 — 활성 경로 모듈 조합으로 발동한 시너지 id 집합(최초 발견 보상 1회 dedup). */
  readonly moduleSynergy: ModuleSynergyController;
  /** 시너지 강화 레벨 — synergyId별 효과 레벨(강화석 영구 성장). */
  readonly synergyLevels: SynergyLevelController;
  readonly spaceship: SpaceshipController;
  readonly upgradeInv: UpgradeInventory;
  readonly grid: ModuleGridController;
  readonly goldBox: GoldModuleBox;
  readonly diamondBox: DiamondUpgradeBox;
  readonly rebirth: RebirthController;
  readonly statUpgrade: StatUpgradeController;
  readonly rebirthUpgrade: StatUpgradeController;
  readonly relic: RelicController;
  readonly dungeon: DungeonController;
  readonly dailyResets: Record<string, number>;
  readonly dailyShop: DailyShopController;
  /** 모듈 융합 일일 카운터 (사용 횟수). GameScene이 onFuse 비용 분기·자정 리셋에 사용. */
  readonly fusion: ModuleFusionController;
  /** 미스터리(?) 블록 일일 보상 카운터 (다이아·유물). BattlefieldRunner가 한도 판정·자정 리셋에 사용. */
  readonly mysteryBlock: MysteryBlockController;
  /** 가이드퀘스트 진행 상태 — SaveData.guideQuest에서 복원. */
  readonly guideQuest: GuideQuestController;
  readonly discovered: string[];
  readonly effectLevel: number;
  /** 도감 마지막 열람 bestChapter — 그 이후 새 해금 조각 있으면 도감 탭 레드닷(codexRedDot). */
  readonly codexSeenChapter: number;
  /** 광고 제거 IAP 보유 여부 (entitlement). GameScene이 ad 슬롯 즉시수령 분기에 사용. */
  readonly adFree: boolean;
  /** 쿨타임 entry 맵 (참조 전달 — CooldownService in-place 갱신이 persist에 반영, dailyResets 동형). */
  readonly cooldowns: Record<string, CooldownEntry>;
  /** 오늘의 광고 다이아 수령 여부 (자정 리셋). */
  readonly shopDailyAdDiamondClaimed: boolean;
  /** 완료 튜토리얼 시나리오 id (참조 전달 — store in-place push가 persist에 반영되도록 GameScene 보유본과 동일 참조 유지). */
  readonly tutorialCompleted: string[];
  /** 튜토리얼 재개 스텝 인덱스 맵 (참조 전달). */
  readonly tutorialResume: Record<string, number>;
  /** 해금 탭 레드닷을 소비한 잠금 탭 키 (참조 전달 — GameScene in-place push가 persist에 반영). */
  readonly tabUnlockSeen: string[];
  /** 읽음 처리한 선택형 다이어로그 id 목록 (참조 전달). */
  readonly dialogueSeenIds: string[];
  /** 분석 마일스톤 — 계정 생애 1회 퍼널 이벤트 dedup (first_fire/first_purchase/rebirth_ready). */
  readonly analyticsMilestones: AnalyticsMilestoneController;
}

/** SaveData → 게임 상태 묶음 (순수 생성; setLanguage 등 side effect는 GameScene이 분배 시 수행). */
export function decodeSaveData(data: ModuleBrickSaveData): GameSaveState {
  return {
    chapter: new ChapterController(data.currentChapter, data.bestChapter),
    economy: new EconomyController(data.gold, data.diamond),
    inventory: ModuleInventory.fromState({ moduleInstances: data.moduleInstances }),
    playerModuleLevels: PlayerModuleLevelController.fromState(data.moduleLevels),
    moduleMastery: ModuleMasteryController.fromState(data.moduleMastery),
    moduleSynergy: ModuleSynergyController.fromState(data.discoveredSynergyIds),
    synergyLevels: SynergyLevelController.fromState(data.synergyLevels),
    spaceship: SpaceshipController.fromState({
      selectedId: data.selectedSpaceshipId,
      ownedIds: data.ownedSpaceshipIds,
      moduleUnlocks: data.spaceshipModuleUnlocks,
    }),
    upgradeInv: UpgradeInventory.fromState(data.upgradeInventory),
    grid: ModuleGridController.fromState(data.moduleGrid),
    goldBox: createGoldBox({
      boxOpenCount: data.boxOpenCount,
      pityCounter: data.pityCounter,
      rarityPityCounter: data.rarityPityCounter,
    }),
    diamondBox: new DiamondUpgradeBox(),
    rebirth: new RebirthController({
      rebirthPoint: data.rebirthPoint,
      rebirthCount: data.rebirthCount,
    }),
    statUpgrade: new StatUpgradeController(data.statUpgradeLevels),
    rebirthUpgrade: new StatUpgradeController(data.rebirthUpgradeLevels, REBIRTH_UPGRADE_DEFS),
    relic: new RelicController(data.relicCounts),
    dungeon: new DungeonController({
      currentIndex: data.dungeonCurrentIndex,
      clearedMaxIndex: data.dungeonClearedMaxIndex,
      ticketsRemaining: data.dungeonTicketsRemaining,
    }),
    dailyResets: data.dailyResets,
    dailyShop: new DailyShopController(Math.random, {
      slots: data.dailyShopSlots,
      freeRerollUsed: data.dailyShopFreeRerollUsed,
      rerollStep: data.dailyShopRerollStep,
    }),
    fusion: new ModuleFusionController(
      { usedCount: data.moduleFusionUsedCount },
      MODULE_BRICK_BALANCE.fusionDiamondCost,
    ),
    mysteryBlock: new MysteryBlockController(
      {
        diamondGrantedToday: data.mysteryDiamondGrantedToday,
        relicGrantedToday: data.mysteryRelicGrantedToday,
      },
      MYSTERY_BRICK.diamondDailyLimit,
      MYSTERY_BRICK.relicDailyLimit,
    ),
    guideQuest: new GuideQuestController(GUIDE_QUEST_DEFINITIONS, data.guideQuest),
    discovered: [...data.discoveredDefinitionIds],
    effectLevel: data.effectLevel,
    codexSeenChapter: data.codexSeenChapter,
    adFree: data.adFree,
    // 참조 전달(복제 금지) — CooldownService(cooldowns)·SaveDataTutorialStore의 in-place 갱신이 GameScene 보유본에 반영되려면 동일 참조여야 함(dailyResets 동형).
    cooldowns: data.cooldowns,
    shopDailyAdDiamondClaimed: data.shopDailyAdDiamondClaimed,
    tutorialCompleted: data.tutorialCompleted,
    tutorialResume: data.tutorialResume,
    tabUnlockSeen: data.tabUnlockSeen,
    dialogueSeenIds: data.dialogueSeenIds ?? [],
    analyticsMilestones: AnalyticsMilestoneController.fromState(data.analyticsMilestones),
  };
}

/** encode 입력 — 현재 게임 상태 묶음 (diamondBox는 직렬화 무관이라 제외, activeTab 포함). */
export interface GameEncodeInput {
  readonly chapter: ChapterController;
  readonly economy: EconomyController;
  readonly inventory: ModuleInventory;
  readonly playerModuleLevels: PlayerModuleLevelController;
  readonly moduleMastery: ModuleMasteryController;
  readonly moduleSynergy: ModuleSynergyController;
  readonly synergyLevels: SynergyLevelController;
  readonly spaceship: SpaceshipController;
  readonly upgradeInv: UpgradeInventory;
  readonly grid: ModuleGridController;
  readonly goldBox: GoldModuleBox;
  readonly rebirth: RebirthController;
  readonly statUpgrade: StatUpgradeController;
  readonly rebirthUpgrade: StatUpgradeController;
  readonly relic: RelicController;
  readonly dungeon: DungeonController;
  readonly dailyResets: Record<string, number>;
  readonly dailyShop: DailyShopController;
  readonly fusion: ModuleFusionController;
  readonly mysteryBlock: MysteryBlockController;
  readonly guideQuest: GuideQuestController;
  readonly discovered: Iterable<string>;
  readonly activeTab: string;
  readonly effectLevel: number;
  readonly codexSeenChapter: number;
  readonly adFree: boolean;
  readonly cooldowns: Record<string, CooldownEntry>;
  readonly shopDailyAdDiamondClaimed: boolean;
  readonly tutorialCompleted: string[];
  readonly tutorialResume: Record<string, number>;
  readonly tabUnlockSeen: string[];
  readonly dialogueSeenIds: string[];
  readonly analyticsMilestones: AnalyticsMilestoneController;
}

/** 게임 상태 묶음 → SaveData (순수 직렬화). nowMs는 호출자가 주입(테스트 결정론 + Date.now 부작용 분리). */
export function encodeSaveData(state: GameEncodeInput, nowMs: number): ModuleBrickSaveData {
  const dailyShopState = state.dailyShop.toState();
  state.spaceship.setModuleUnlockedCells(state.spaceship.selectedId, state.grid.toState().unlockedCells);
  const spaceshipState = state.spaceship.toState();
  return {
    version: MODULE_BRICK_SAVE_VERSION,
    gold: state.economy.gold.toString(),
    diamond: state.economy.diamond.toString(),
    currentChapter: state.chapter.currentChapter,
    bestChapter: state.chapter.bestChapter,
    moduleInstances: state.inventory.toState().moduleInstances,
    moduleLevels: state.playerModuleLevels.toState(),
    moduleMastery: state.moduleMastery.toState(),
    upgradeInventory: state.upgradeInv.toState(),
    discoveredDefinitionIds: [...state.discovered],
    discoveredSynergyIds: state.moduleSynergy.toState(),
    synergyLevels: state.synergyLevels.toState(),
    selectedSpaceshipId: spaceshipState.selectedId,
    ownedSpaceshipIds: [...spaceshipState.ownedIds],
    spaceshipModuleUnlocks: Object.fromEntries(
      Object.entries(spaceshipState.moduleUnlocks).map(([id, cells]) => [
        id,
        cells.map((cell) => ({ ...cell })),
      ]),
    ),
    moduleGrid: state.grid.toState(),
    rebirthPoint: state.rebirth.rebirthPoint,
    rebirthCount: state.rebirth.rebirthCount,
    statUpgradeLevels: state.statUpgrade.toState(),
    rebirthUpgradeLevels: state.rebirthUpgrade.toState(),
    relicCounts: state.relic.toState(),
    dungeonCurrentIndex: state.dungeon.currentIndex,
    dungeonClearedMaxIndex: state.dungeon.clearedMaxIndex,
    dungeonTicketsRemaining: state.dungeon.ticketsRemaining,
    dailyResets: state.dailyResets,
    dailyShopSlots: dailyShopState.slots,
    dailyShopFreeRerollUsed: dailyShopState.freeRerollUsed,
    dailyShopRerollStep: dailyShopState.rerollStep,
    moduleFusionUsedCount: state.fusion.toState().usedCount,
    mysteryDiamondGrantedToday: state.mysteryBlock.toState().diamondGrantedToday,
    mysteryRelicGrantedToday: state.mysteryBlock.toState().relicGrantedToday,
    guideQuest: state.guideQuest.toState(),
    boxOpenCount: state.goldBox.boxOpenCount,
    pityCounter: state.goldBox.pityCounter,
    rarityPityCounter: state.goldBox.rarityPityCounter,
    selectedTab: state.activeTab,
    effectLevel: state.effectLevel,
    codexSeenChapter: state.codexSeenChapter,
    adFree: state.adFree,
    cooldowns: state.cooldowns,
    shopDailyAdDiamondClaimed: state.shopDailyAdDiamondClaimed,
    tutorialCompleted: state.tutorialCompleted,
    tutorialResume: state.tutorialResume,
    tabUnlockSeen: state.tabUnlockSeen,
    dialogueSeenIds: state.dialogueSeenIds,
    analyticsMilestones: state.analyticsMilestones.toState(),
    lastSavedAtUtcMs: nowMs,
  };
}
